<template>
  <div>
    <!--    title-->
    <v-row
      style="border-bottom: 1px solid #E5E7EB;"
      class="margin-0"
    >
      <v-col
        class="d-flex justify-start block-title"
      >
        <div
          class="block-title--wrapper"
        >
          {{ item.name || defaultEmptyValue }}
        </div>
      </v-col>

      <v-col
        class="d-flex justify-end"
      >
        <table-row-actions-menu
          :data-index="itemIndex"
          :config="actionsConfig"
          :table-data="data"
        />
      </v-col>
    </v-row>

    <!--    visible data section-->
    <v-row
      v-for="(dataObject, dataObjIndex) in getVisibleItemDetails()"
      :key="dataObjIndex"
      class="margin-0"
    >
      <v-col
        class="data-label"
      >
        {{ dataObject.label }}
      </v-col>

      <v-col
        class="d-flex justify-end data-value"
      >
        {{ dataObject.value }}
      </v-col>
    </v-row>

    <!--    hidden data section-->
    <template
      v-if="slidedDown"
    >
      <v-row
        v-for="(dataObject, dataObjIndex) in getAllItemDetails()"
        :key="'hidden_' + dataObjIndex"
        class="margin-0"
      >
        <v-col
          class="data-label"
        >
          {{ dataObject.label }}
        </v-col>

        <v-col
          class="d-flex justify-end data-value"
        >
          <template
            v-if="dataObject.key === 'alarm_on'"
          >
            <cell-icon
              :cell-data="dataObject.value"
              :config="dataObject"
              :row-data="item"
            />
          </template>
          <template
            v-else
          >
            {{ dataObject.value }}
          </template>
        </v-col>
      </v-row>
    </template>

    <!--    arrow up/down button for showing more details-->
    <image-field
      :src="slidedDown ? '/img/icons/arrow-up-base.svg' : '/img/icons/arrow-down-base.svg'"
      class="arrowUpDown"
      @onClick="slidedDown = !slidedDown"
    />

    <information-dialog
      :open-dialog="informationDialogConfig.openDialog"
      :title="informationDialogConfig.title"
      :dialog-message="informationDialogConfig.message"
      :positive-button-text="informationDialogConfig.positiveBtnText"
      :negative-button-text="informationDialogConfig.negativeBtnText"
      :confirm-callback-function="informationDialogConfig.callbackFn"
      @close="$set(informationDialogConfig, 'openDialog', false)"
    />

    <!--      archive service interval dialog-->
    <index-dialog
      v-if="archiveIntervalDialogConfig.openDialog"
      :open-dialog="archiveIntervalDialogConfig.openDialog"
      :title="archiveIntervalDialogConfig.title"
      :positive-button-text="archiveIntervalDialogConfig.positiveBtnText"
      :negative-button-text="archiveIntervalDialogConfig.negativeBtnText"
      :confirm-callback-function="archiveIntervalDialogConfig.callbackFn"
      @close="$set(archiveIntervalDialogConfig, 'openDialog', false)"
    >
      <archive-service-interval
        :validation-errors="archiveValidationErrors"
        :type="archiveType"
        @update-data="onIntervalArchiveDataUpdate"
        @clear-validation-errors="key => $delete(archiveValidationErrors, key)"
        @remove-all-validation-errors="archiveValidationErrors = {}"
      />
    </index-dialog>
  </div>
</template>

<script>
import ImageField from '@/global/components/fields/ImageField.vue'
import TableRowActionsMenu from '@/global/components/table/components/TableRowActionsMenu.vue'
import InformationDialog from '@/global/components/dialogs/InformationDialog.vue'
import { dateService, FORMATS } from '@/global/services/date'
import CellIcon from '../../components/main-table/CellIcon.vue'
import vehicleServiceIntervalMixin from '../../mixins/vehicleServiceIntervalMixin'
import ArchiveServiceInterval from '../../components/archiveServiceInterval.vue'
import IndexDialog from '@/global/components/dialogs/IndexDialog.vue'
import { showDetailsAction, deleteAction } from '@/global/components/table/helpers/tableActions'

const DEFAULT_EMPTY_VALUE = '--'
export default {
  name: 'VehicleServiceIntervalGridBlock',

  components: {
    IndexDialog,
    ArchiveServiceInterval,
    CellIcon,
    InformationDialog,
    TableRowActionsMenu,
    ImageField
  },

  mixins: [
    vehicleServiceIntervalMixin
  ],

  props: {
    data: {
      type: Array,
      default: () => ([])
    },
    item: {
      type: Object,
      required: true,
      default: () => ({})
    },
    itemIndex: {
      type: Number,
      default: 0
    },
    blockConfig: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      slidedDown: false,
      informationDialogConfig: {
        openDialog: false,
        message: this.$t('fleet/vehicle.confirm_delete'),
        title: '',
        positiveBtnText: '',
        negativeBtnText: this.$t('base.cancel'),
        callbackFn: () => {}
      },
      defaultEmptyValue: DEFAULT_EMPTY_VALUE,
      archiveIntervalDialogConfig: {
        openDialog: false,
        title: this.$t('fleet/vehicle/vehicle_service_interval.archive_dialog_title'),
        positiveBtnText: this.$t('base.archive'),
        negativeBtnText: this.$t('base.cancel'),
        callbackFn: () => this.archiveServiceInterval()
      },
      archiveId: null,
      archiveFormData: {},
      archiveValidationErrors: {},
      archiveType: null
    }
  },

  computed: {
    actionsConfig () {
      return {
        tableConfig: {
          actionsConfig: [
            showDetailsAction(this.$t, (event) => this.onShowServiceIntervalDetails(event)),
            {
              label: this.$t('fleet/vehicle_service.archive_service'),
              src: '/img/icons/report-icon.svg',
              click: (event) => this.onArchiveServiceInterval(event)
            },
            deleteAction(this.$t, (event) => this.onIntervalDelete(event))
          ]
        }
      }
    }
  },

  methods: {
    getVisibleItemDetails () {
      return [
        {
          label: this.$t('fleet/vehicle_service.service_type'),
          value: this.item?.type?.description ? this.$t(this.item.type.description) : DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_service.start_state'),
          value: (this.item?.type?.name === 'km' || this.item?.type?.name === 'hours') && this.item?.start_state
            ? this.item.start_state + ' ' + (this.item?.unit || '')
            : dateService.formatFromUtc(this.item.start_state, FORMATS.date)
        },
        {
          label: this.$t('fleet/vehicle_service.interval'),
          value: this.item?.interval || DEFAULT_EMPTY_VALUE
        }
      ]
    },

    getAllItemDetails () {
      return [
        {
          label: this.$t('fleet/vehicle_service.current_state'),
          value: (this.item?.type?.name === 'km' || this.item?.type?.name === 'hours')
            ? this.blockConfig?.showItem?.item?.odometer || ''
            : dateService.formatFromUtc(this.item.current_state, FORMATS.date)
        },
        {
          label: this.$t('fleet/vehicle_service.until_next_service'),
          value: (this.item?.type?.name === 'km' || this.item?.type?.name === 'hours') && this.item?.start_state && this.item?.interval && this.blockConfig?.showItem?.item?.odometer_value
            ? (this.item.start_state + this.item.interval - this.blockConfig?.showItem.item.odometer_value) + ' ' + (this.item?.unit || '')
            : (this.item?.until_next_service || '') + ' ' + (this.item?.unit && this.item?.until_next_service ? this.item.unit : '')
        },
        {
          label: this.$t('fleet/vehicle_service.upcoming_service'),
          value: (this.item?.type?.name === 'km' || this.item?.type?.name === 'hours') && this.item?.start_state && this.item?.interval
            ? (this.item.start_state + this.item.interval) + ' ' + (this.item?.unit || '')
            : dateService.formatFromUtc(this.item.upcoming_service, FORMATS.date)
        }
      ]
    }
  }
}
</script>

<style scoped lang="scss">
.block-title {
  display: flex;
  justify-content: flex-start;
  font-family: 'Satoshi', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  color: #111827;
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
}

.block-title--wrapper {
  display: flex;
  height: 48px;
  align-items: center;
}

.data-label {
  font-family: 'Satoshi', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: #6B7280;
}

.data-value {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: #111827;
  text-align: right;
}

.arrowUpDown {
  position: absolute;
  bottom: 10px;
  left: 50%;
}

.arrowUpDown:hover {
  cursor: pointer;
}
</style>
